// BlogPage.js
import React from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { Calendar, Clock, ArrowRight, Search, Filter, ArrowLeft } from 'lucide-react';

const BlogPage = () => {
  const blogPosts = [
    {
      id: 1,
      date: 'March 10, 2024',
      title: 'Studying Abroad: Working Backwards From the Deadline',
      excerpt: 'Country, institution and course are really three separate decisions, and taking them in the wrong order is what makes the whole thing feel unmanageable. Here is the sequence we use with clients, from the budget conversation through to the day you accept an offer.',
      image: 'blog-1.jpg',
      link: '/blog/practical-guide-studying-abroad',
      readTime: '6 min read',
      category: 'Studying'
    },
    {
      id: 2,
      date: 'July 18, 2024',
      title: 'Taking a Job Overseas: What to Sort Out, and in Which Order',
      excerpt: 'Most people start with the job adverts and hit the immigration rules later, which is the expensive way round. This piece runs the other way: visa route first, then your qualifications, then the market, then the interview.',
      image: 'blog-2.jpg',
      link: '/blog/working-abroad-preparation',
      readTime: '7 min read',
      category: 'Working'
    },
    {
      id: 3,
      date: 'July 24, 2024',
      title: 'Should You Go At All? A Sober Look Before You Commit',
      excerpt: 'Before the applications and the visa forms, there is a plainer question worth sitting with. We look at the real cost, the honest timeline, the reasons people give for going, and the points at which it is still sensible to change your mind.',
      image: 'blog-3.jpg',
      link: '/blog/informed-decisions-study-work-abroad',
      readTime: '7 min read',
      category: 'Deciding'
    }
  ];

  const categories = ['All', 'Studying', 'Working', 'Deciding'];

  return (
    <div className="min-h-screen bg-white">
      {/* Hero Section */}
      <section className="relative py-20 bg-gradient-to-br from-primary to-secondary">
        <div className="max-w-4xl mx-auto px-4 md:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="text-center text-white"
          >
            <h1 className="text-4xl md:text-5xl font-semibold mb-6 leading-tight">
              Notes and Guides
            </h1>
            <p className="text-xl font-light leading-relaxed text-white/90 max-w-3xl mx-auto">
              Longer pieces on the questions clients bring to us: how the routes actually work, what the paperwork demands, how long each stage takes, and where the process most often goes wrong.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Content Section */}
      <section className="py-16">
        <div className="max-w-7xl mx-auto px-4 md:px-8">
          {/* Category Filter */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="mb-12"
          >
            <div className="flex flex-wrap items-center gap-4">
              <div className="flex items-center gap-2 text-gray-600">
                <Filter size={18} />
                <span className="font-medium">Show me:</span>
              </div>
              <div className="flex flex-wrap gap-2">
                {categories.map((category, index) => (
                  <button
                    key={index}
                    className={`px-4 py-2 rounded-full text-sm font-medium transition ${
                      index === 0
                        ? 'bg-primary text-white'
                        : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                    }`}
                  >
                    {category}
                  </button>
                ))}
              </div>
            </div>
          </motion.div>

          {/* Blog Posts Grid */}
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {blogPosts.map((post, index) => (
              <motion.div
                key={post.id}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6, delay: index * 0.1 }}
              >
                <Link to={post.link} className="group block h-full">
                  <article className="bg-white rounded-lg overflow-hidden shadow-sm hover:shadow-lg transition-shadow duration-300 h-full flex flex-col">
                    {/* Image */}
                    <div className="relative h-48 overflow-hidden">
                      <div 
                        className="absolute inset-0 bg-cover bg-center transform group-hover:scale-105 transition-transform duration-500"
                        style={{ 
                          backgroundImage: `url(${require(`../assets/${post.image}`)})`,
                        }}
                      />
                      <div className="absolute inset-0 bg-gradient-to-t from-black/50 to-transparent"></div>
                      
                      {/* Category Badge */}
                      <div className="absolute top-4 left-4">
                        <span className="bg-white/90 backdrop-blur-sm text-primary text-xs font-medium px-3 py-1 rounded-full">
                          {post.category}
                        </span>
                      </div>
                    </div>

                    {/* Content */}
                    <div className="p-6 flex flex-col flex-grow">
                      {/* Meta */}
                      <div className="flex items-center gap-4 text-sm text-gray-500 mb-4">
                        <div className="flex items-center gap-1">
                          <Calendar size={14} />
                          <span>{post.date}</span>
                        </div>
                        <div className="flex items-center gap-1">
                          <Clock size={14} />
                          <span>{post.readTime}</span>
                        </div>
                      </div>

                      {/* Title */}
                      <h3 className="text-xl font-semibold text-primary mb-3 leading-tight group-hover:text-primary-hover transition">
                        {post.title}
                      </h3>

                      {/* Excerpt */}
                      <p className="text-gray-600 text-sm leading-relaxed mb-6 flex-grow">
                        {post.excerpt}
                      </p>

                      {/* Read More */}
                      <div className="flex items-center gap-2 text-primary text-sm font-medium group-hover:gap-3 transition-all">
                        <span>Read this</span>
                        <ArrowRight size={16} className="group-hover:translate-x-1 transition-transform" />
                      </div>
                    </div>
                  </article>
                </Link>
              </motion.div>
            ))}
          </div>

          {/* Newsletter Section */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6, delay: 0.3 }}
            className="mt-20"
          >
            <div className="bg-gradient-to-r from-primary to-secondary rounded-2xl p-8 md:p-12 text-center text-white">
              <h2 className="text-3xl font-semibold mb-4">
                Get New Pieces by Email
              </h2>
              <p className="text-white/90 mb-8 max-w-2xl mx-auto">
                We send a note when something new goes up. No more than that, and you can stop at any point.
              </p>
              <div className="flex flex-col sm:flex-row gap-4 max-w-md mx-auto">
                <input
                  type="email"
                  placeholder="Your email address"
                  className="flex-grow px-4 py-3 rounded-lg text-gray-800 placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-white/50"
                />
                <button className="bg-white text-primary px-6 py-3 rounded-lg font-medium hover:bg-gray-100 transition">
                  Subscribe
                </button>
              </div>
            </div>
          </motion.div>

          {/* Back to Home */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6, delay: 0.4 }}
            className="mt-12 text-center"
          >
            <Link 
              to="/" 
              className="inline-flex items-center gap-2 text-primary hover:text-primary-hover transition font-medium"
            >
              <ArrowLeft size={20} />
              Back to the homepage
            </Link>
          </motion.div>
        </div>
      </section>
    </div>
  );
};

export default BlogPage;
